import React from "react";
import { type DatasetUsage, datasetUsageTotal } from "@/lib/api/datasets";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/// One line: "In 3 datasets: Eurostat (public), Markets · 1 private".
export const datasetUsageSummary = (usage: DatasetUsage): string => {
  const names = usage.named.map((d) =>
    d.is_public ? `${d.name} (public)` : d.name,
  );
  const hidden =
    usage.private_count > 0
      ? [`${plural(usage.private_count, "private dataset")} of other people`]
      : [];
  return `In ${plural(datasetUsageTotal(usage), "dataset")}: ${[...names, ...hidden].join(", ")}`;
};

/// A deletion never edits a dataset — each keeps the table listed as deleted —
/// so this warns rather than blocks.
export const DatasetUsageNote: React.FC<{ usage: DatasetUsage }> = ({
  usage,
}) => {
  const total = datasetUsageTotal(usage);
  if (total === 0) return null;
  return (
    <div className="mt-3" data-testid="dataset-usage-note">
      <p>It belongs to {plural(total, "dataset")}:</p>
      <ul className="mt-1 list-disc pl-5 text-fg-medium">
        {usage.named.map((d) => (
          <li key={d.id} data-dataset-id={d.id}>
            {d.name}
            {d.is_public ? " (public)" : ""}
          </li>
        ))}
        {usage.private_count > 0 && (
          <li>
            {plural(usage.private_count, "private dataset")} of people you
            shared it with
          </li>
        )}
      </ul>
      <p className="mt-2 text-fg-muted-regular-small">
        Each keeps the table listed, marked deleted once it is gone. Move it to
        another model instead to keep it in them.
      </p>
    </div>
  );
};
